import { useEffect, useState } from 'react';
import { useT } from '@dia/i18n';
import { cacheKey, getDb } from '@dia/db';
import { supabase } from '../../supabase';

/**
 * A room photograph, when there is one.
 *
 * There are none yet. Until they are taken, the card draws a marked
 * placeholder rather than an empty box, so nobody mistakes a half finished
 * page for the finished one. The photographs go to the reference-photos
 * bucket under rooms/, and migration 016 opens exactly that prefix to a guest.
 *
 * Signed URLs change on every request, so the browser cache cannot hold them.
 * The blob is kept in IndexedDB keyed by its storage path instead, which is
 * how the ops shell already does its reference photos, so a room seen once
 * opens again with no signal.
 */

async function roomPhotoBlob(path: string): Promise<Blob | null> {
  const db = getDb();
  const key = cacheKey('roomphoto', path);
  const hit = await db.cache.get(key);
  if (hit && hit.row instanceof Blob) return hit.row;

  try {
    const { data, error } = await supabase.storage.from('reference-photos').download(path);
    if (error || !data) return null;
    await db.cache.put({ key, entity: 'roomphoto', id: path, row: data, cachedAt: Date.now() });
    return data;
  } catch {
    return null;
  }
}

export function RoomPhoto({ path, roomName }: { path: string | null; roomName: string }) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    let alive = true;
    let made: string | null = null;
    void roomPhotoBlob(path).then((blob) => {
      if (!alive || !blob) return;
      made = URL.createObjectURL(blob);
      setUrl(made);
    });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path]);

  if (path && url) {
    return (
      <img
        src={url}
        alt={t('guest.rooms.photo_alt', { room: roomName })}
        className="h-28 w-full rounded object-cover"
      />
    );
  }

  return (
    <div className="flex h-28 w-full items-end rounded bg-surfaceDeep p-2">
      <p className="text-caption text-inkFaint">{t('guest.rooms.photo_missing')}</p>
    </div>
  );
}
